/** @format */

import { useQuery } from '@apollo/client';
import { GET_POLICIES, Policy, InsurancePoliciesFilterInput } from '../services/getPolicies';

/**
 * Filter out empty, null, or undefined policy objects
 */
const filterEmptyPolicies = (policies: Policy[]): Partial<Policy>[] => {
  if (!Array.isArray(policies)) {
    return [];
  }

  return policies.map((policy) => ({
    ...policy,
    majorMed: policy.majorMed && Object.values(policy.majorMed).some((v) => v) ? policy.majorMed : undefined,
    privateMed: policy.privateMed && Object.values(policy.privateMed).some((v) => v) ? policy.privateMed : undefined,
    ancillaries: policy.ancillaries?.filter((a) => Object.values(a).some((v) => v)) || undefined,
  }));
};

/**
 * Custom hook to fetch policies with filtering
 * Handles all three policy types (majorMed, privateMed, ancillaries) in a single query
 */
export const usePolicies = (filter?: InsurancePoliciesFilterInput) => {
  const { data, loading, error, refetch } = useQuery<{ getPolicies: Policy[] }>(GET_POLICIES, {
    variables: { filter: filter || {} },
    skip: false,
  });

  const policies = data ? filterEmptyPolicies(data.getPolicies) : [];

  return {
    policies,
    loading,
    error,
    refetch,
  };
};



/**
 * Custom hook to fetch ACA request policies
 * Filters policies where request is true and focuses on majorMed policies with state
 */
export const useACARequestPolicies = (filter?: InsurancePoliciesFilterInput) => {
  const mergedFilter = {
    ...filter,
    request: true,
  };

  const { data, loading, error, refetch } = useQuery<{ getPolicies: Policy[] }>(GET_POLICIES, {
    variables: { filter: mergedFilter },
  });

  // Filter to only include policies with majorMed data and remove privateMed and ancillaries
  const policies = data
    ? filterEmptyPolicies(data.getPolicies)
        .filter((policy) => policy.majorMed && Object.values(policy.majorMed).some((v) => v))
        .map((policy) => ({
          ...policy,
          privateMed: undefined,
          ancillaries: undefined,
        }))
    : [];

  return {
    policies,
    loading,
    error,
    refetch,
  };
};
